import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {interval, Observable, of, Subscription} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {Vehicle} from './vehicle';
import {ApiUtils} from '@shared/utils/api.utils';
import {VehicleCalculate} from './vehicle-calculate';
import {VehicleStatus} from './vehicle-status';
import {VehicleTransactionExt} from './vehicle-transaction-ext';
import {ListFilter, ListResult, Pagination} from "@shared/base";
import {ConfigUtil} from '@shared/services/config.util';

@Injectable({
  providedIn: 'root'
})
export class VehicleApi implements OnDestroy {
  private readonly httpClient = inject(HttpClient);

  private readonly endpoint: string;
  private cacheById: Map<number, Vehicle> = new Map<number, Vehicle>();
  private cacheClearSubscription?: Subscription;

  constructor() {
    this.endpoint = `${ConfigUtil.get('api')['backend']}/vehicles`;

    // Clear the cache every 5 minutes
    this.cacheClearSubscription = interval(5 * 60 * 1000).subscribe(() => this.clearCache());
  }

  public findByFilter(listFilter: ListFilter, pagination: Pagination): Observable<ListResult<Vehicle>> {
    const httpParams = ApiUtils.createHttpParams({...listFilter, ...pagination.toListFilter()});

    return this.httpClient.get<ListResult<Vehicle>>(`${this.endpoint}/search`, {params: httpParams}).pipe(map(value => {
      value.data.forEach(vehicle => {
        if (!vehicle['@type']) {
          vehicle['@type'] = 'VehicleDto';
        }

        if (vehicle.firstIssueDate) {
          vehicle.firstIssueDate = new Date(vehicle.firstIssueDate);
        }
        if (vehicle.firstAdmissionDate) {
          vehicle.firstAdmissionDate = new Date(vehicle.firstAdmissionDate);
        }
      });
      return Object.assign(new ListResult<Vehicle>(), value);
    }));
  }

  public findById(id: number): Observable<Vehicle> {
    return this.httpClient.get<Vehicle>(`${this.endpoint}/${id}`)
      .pipe(
        map(value => {
          return Object.assign(new Vehicle(), value);
        }),
        tap(vehicle => this.cacheById.set(vehicle.id, vehicle))
      );
  }

  public findByIdCached(id: number): Observable<Vehicle> {
    if (this.cacheById.has(id)) {
      return of(this.cacheById.get(id));
    } else {
      return this.findById(id);
    }
  }

  public save(vehicle: Vehicle): Observable<Vehicle> {
    this.clearCache();
    return this.httpClient.post<Vehicle>(`${this.endpoint}`, vehicle).pipe(map(value => {
      return Object.assign(new Vehicle(), value);
    }));
  }

  public update(vehicle: Vehicle): Observable<boolean> {
    this.clearCache();
    return this.httpClient.put<boolean>(`${this.endpoint}/${vehicle.id}`, vehicle);
  }

  public calculate(vehicleCalculate: VehicleCalculate): Observable<VehicleCalculate> {
    return this.httpClient.post<VehicleCalculate>(`${this.endpoint}/calculate`, vehicleCalculate).pipe(map(value => {
      return Object.assign(new VehicleCalculate(), value);
    }));
  }

  public status(vrn: string): Observable<VehicleStatus> {
    return this.httpClient.get<VehicleStatus>(`${this.endpoint}/${vrn}/status`);
  }

  public transactions(vrn: string): Observable<VehicleTransactionExt[]> {
    return this.httpClient.get<VehicleTransactionExt[]>(`${this.endpoint}/${vrn}/transactions`);
  }

  private clearCache() {
    this.cacheById = new Map<number, Vehicle>();
  }

  ngOnDestroy(): void {
    if (this.cacheClearSubscription) {
      this.cacheClearSubscription.unsubscribe();
    }
  }
}
